import React, { useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { pick } from "./GameShell";

/**
 * Quiz — multi-level multiple-choice game.
 * Level provides {questions:[{q,{options:[{...,correct}]}]} from levels[level-1].
 * Points = round(correct/total * pointsPerLevel).
 */
const Quiz = ({ game, level, lang, onFinish }) => {
  const pool = game.levels[level - 1]?.questions || game.data.questions;
  // Shuffle questions for replayability
  const questions = useMemo(() => [...pool].sort(() => Math.random() - 0.5), [pool, level]);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [picked, setPicked] = useState(null);
  const total = questions.length;
  const q = questions[i];
  const pointsPerLevel = (game.levels[level - 1]?.points) || game.points;

  const choose = (opt, idx) => {
    if (picked !== null) return;
    setPicked(idx);
    const correct = !!opt.correct;
    if (correct) setScore((s) => s + 1);
    setTimeout(() => {
      if (i + 1 < total) {
        setI(i + 1);
        setPicked(null);
      } else {
        const finalScore = score + (correct ? 1 : 0);
        const earned = Math.round((finalScore / total) * pointsPerLevel);
        onFinish({ score: finalScore, points: earned, total });
      }
    }, 700);
  };

  return (
    <div data-testid={`game-quiz-${game.id}`}>
      <div className="flex justify-between text-xs bs-muted mb-2">
        <span>{i + 1}/{total}</span>
        <span>{lang === "es" ? "Aciertos" : "Score"}: {score}</span>
      </div>
      <p className="bs-title font-bold text-lg mb-4">{pick(lang, q.q)}</p>
      <div className="space-y-2">
        {q.options.map((opt, idx) => {
          const showResult = picked !== null;
          let cls = "w-full text-left p-3 sm:p-4 rounded-2xl border transition-colors bg-white/5 border-white/10 hover:bg-white/10 text-white";
          if (showResult && idx === picked) {
            cls = `w-full text-left p-3 sm:p-4 rounded-2xl border transition-colors ${opt.correct ? "bg-emerald-500/25 border-emerald-400" : "bg-rose-500/25 border-rose-400"} text-white`;
          } else if (showResult && opt.correct) {
            cls = "w-full text-left p-3 sm:p-4 rounded-2xl border transition-colors bg-emerald-500/15 border-emerald-400/50 text-white";
          }
          return (
            <button
              key={idx}
              onClick={() => choose(opt, idx)}
              disabled={picked !== null}
              className={cls}
              data-testid={`quiz-option-${idx}`}
            >
              <span className="font-semibold text-sm sm:text-base">{pick(lang, opt)}</span>
              {showResult && idx === picked && (
                <span className="ml-2 inline-flex items-center text-xs">
                  {opt.correct ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default Quiz;
